import React, { useEffect, useState } from "react";
import axiosInstance from "../../../Configuration/axiosInstance";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import styles from "./WarehousePrep.module.css";

dayjs.extend(utc);
dayjs.extend(timezone);

const WarehousePrep = () => {
  const [deliveryRuns, setDeliveryRuns] = useState([]);
  // ✅ NEW: State for pickup orders
  const [pickupOrders, setPickupOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDate, setSelectedDate] = useState("tomorrow");
  const [currentDate, setCurrentDate] = useState("");
  const [tomorrowDate, setTomorrowDate] = useState("");

  useEffect(() => {
    const today = dayjs().tz("Australia/Sydney").format("YYYY-MM-DD");
    const tomorrow = dayjs().tz("Australia/Sydney").add(1, "day").format("YYYY-MM-DD");
    
    setCurrentDate(today);
    setTomorrowDate(tomorrow);
  }, []);

  useEffect(() => {
    if (currentDate && tomorrowDate) {
      fetchDeliveryRuns();
      // ✅ NEW: Also fetch pickup orders
      fetchPickupOrders();
    }
  }, [selectedDate, currentDate, tomorrowDate]);

  const fetchDeliveryRuns = async () => {
    try {
      setLoading(true);
      setError(null);

      const targetDate = selectedDate === "today" ? currentDate : tomorrowDate;

      const res = await axiosInstance.get("/delivery", {
        params: {
          fromDate: targetDate,
          toDate: targetDate,
          limit: 100,
        },
      });

      console.log("Raw delivery data:", res.data.data);
      const processedRuns = processDeliveryData(res.data.data || []);
      setDeliveryRuns(processedRuns);
    } catch (error) {
      console.error("Error fetching delivery runs:", error);
      setError(`Failed to fetch delivery runs for ${selectedDate}`);
    }
  };

  // ✅ NEW: Fetch pickup orders for the selected date
  const fetchPickupOrders = async () => {
    try {
      const targetDate = selectedDate === "today" ? currentDate : tomorrowDate;

      // Get all orders for the target date
      const res = await axiosInstance.get("/order", {
        params: {
          date: targetDate,
          limit: 100,
        },
      });

      // Filter for pickup orders only (isPickup: true)
      const allOrders = res.data.data || [];
      const pickupOrdersForDate = allOrders.filter(order => {
        const orderDate = dayjs(order.date).format("YYYY-MM-DD");
        return orderDate === targetDate && order.isPickup === true;
      });

      console.log(`📦 Found ${pickupOrdersForDate.length} pickup orders for ${targetDate}`);
      
      // Process pickup orders to aggregate products
      const processedPickupData = processPickupOrders(pickupOrdersForDate);
      setPickupOrders(processedPickupData);

    } catch (error) {
      console.error("Error fetching pickup orders:", error);
      // Don't overwrite delivery error - just log pickup error
      if (!error.message?.includes("delivery")) {
        setError(prev => prev || `Failed to fetch pickup orders for ${selectedDate}`);
      }
    } finally {
      setLoading(false);
    }
  };

  // ✅ NEW: Process pickup orders to aggregate products
  const processPickupOrders = (orders) => {
    const aggregatedProducts = {};
    let totalOrders = orders.length;
    let totalCustomers = new Set();
    const orderComments = [];

    orders.forEach((order) => {
      // Collect customer info
      if (order.customer?.businessName) {
        totalCustomers.add(order.customer.businessName);
      }

      // Collect order comments
      if (order.comments && order.comments.trim() !== '') {
        orderComments.push({
          orderNumber: order.orderNumber,
          comment: order.comments.trim(),
          businessName: order.customer?.businessName || 'Unknown Customer',
        });
      }

      // Aggregate products
      order.products?.forEach((productItem) => {
        if (productItem.product?.productName) {
          const productName = productItem.product.productName;
          const weight = productItem.product.weight || 0;
          const quantity = productItem.quantity || 0;

          if (!aggregatedProducts[productName]) {
            aggregatedProducts[productName] = {
              name: productName,
              totalQuantity: 0,
              totalWeight: 0,
              unit: productItem.product.unit || "kg",
              sku: productItem.product.sku || '',
              orders: new Set(),
            };
          }

          aggregatedProducts[productName].totalQuantity += quantity;
          aggregatedProducts[productName].totalWeight += weight * quantity;
          aggregatedProducts[productName].orders.add(order.orderNumber);
        }
      });
    });

    // Sort order comments by order number
    orderComments.sort((a, b) => a.orderNumber - b.orderNumber);

    // Convert products to array with order info
    const productsArray = Object.values(aggregatedProducts).map(product => ({
      ...product,
      orders: Array.from(product.orders),
      orderCount: product.orders.size
    }));

    return {
      orders,
      aggregatedProducts: productsArray,
      totalOrders,
      totalCustomers: totalCustomers.size,
      customerNames: Array.from(totalCustomers),
      orderComments,
      hasOrderComments: orderComments.length > 0,
      totalWeight: productsArray.reduce((total, product) => total + product.totalWeight, 0)
    };
  };

  // ✅ UPDATED: Process delivery data with new order structure
  const processDeliveryData = (deliveries) => {
    const processedData = deliveries.map((delivery) => {
      const allProducts = {};
      let totalOrders = 0;
      let totalCustomers = new Set();
      const orderComments = [];

      console.log("Processing delivery:", delivery.run, "with orders:", delivery.order);

      delivery.order?.forEach((orderItem, index) => {
        const order = orderItem.orderId;
        
        if (!order) {
          console.warn(`Order data missing for orderItem at index ${index}:`, orderItem);
          return;
        }

        totalOrders++;
        
        if (order.customer?.businessName) {
          totalCustomers.add(order.customer.businessName);
        }

        if (order.comments && order.comments.trim() !== '') {
          orderComments.push({
            orderNumber: order.orderNumber,
            comment: order.comments.trim(),
            businessName: order.customer?.businessName || 'Unknown Customer',
            displayOrder: orderItem.displayOrder || index + 1
          });
        }

        order.products?.forEach((productItem) => {
          if (productItem.product?.productName) {
            const productName = productItem.product.productName;
            const weight = productItem.product.weight || 0;
            const quantity = productItem.quantity || 0;

            if (!allProducts[productName]) {
              allProducts[productName] = {
                name: productName,
                totalQuantity: 0,
                totalWeight: 0,
                unit: productItem.product.unit || "kg",
                sku: productItem.product.sku || '',
                orders: new Set(),
              };
            }

            allProducts[productName].totalQuantity += quantity;
            allProducts[productName].totalWeight += weight * quantity;
            allProducts[productName].orders.add(order.orderNumber);
          }
        });
      });

      orderComments.sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));

      const productsWithOrderInfo = Object.values(allProducts).map(product => ({
        ...product,
        orders: Array.from(product.orders),
        orderCount: product.orders.size
      }));

      return {
        ...delivery,
        aggregatedProducts: productsWithOrderInfo,
        totalOrders,
        totalCustomers: totalCustomers.size,
        customerNames: Array.from(totalCustomers),
        orderComments,
        hasOrderComments: orderComments.length > 0
      };
    });

    console.log("Processed delivery data:", processedData);
    return processedData;
  };

  const formatProductsList = (products) => {
    return products.map((product) => (
      <div key={product.name} className={styles.productItem}>
        <span className={styles.productName}>{product.name}</span>
        <span className={styles.productQuantity}>
          {product.totalQuantity} {product.unit}
          {product.totalWeight > 0 && (
            <span className={styles.productWeight}>
              ({product.totalWeight.toFixed(1)}kg total)
            </span>
          )}
          {product.orders && product.orders.length > 0 && (
            <span className={styles.productOrders}>
              - Orders: {product.orders.join(', ')}
            </span>
          )}
        </span>
      </div>
    ));
  };

  const getTotalWeight = (products) => {
    return products.reduce((total, product) => total + product.totalWeight, 0);
  };

  const handleDateToggle = (dateOption) => {
    setSelectedDate(dateOption);
  };

  const getDateDisplayInfo = () => {
    if (selectedDate === "today") {
      return {
        date: currentDate,
        label: "Today",
        dayName: dayjs(currentDate).format("dddd"),
      };
    } else {
      return {
        date: tomorrowDate,
        label: "Tomorrow", 
        dayName: dayjs(tomorrowDate).format("dddd"),
      };
    }
  };

  const dateInfo = getDateDisplayInfo();

  const formatDriversList = (delivery) => {
    const drivers = [];
    
    if (delivery.primaryDriver) {
      const primaryName = delivery.primaryDriver.name || 
                         delivery.primaryDriver.email?.split('@')[0] || 
                         'Primary Driver';
      drivers.push(`${primaryName} (Primary)`);
    }
    
    if (delivery.assignedUsers && delivery.assignedUsers.length > 0) {
      const additionalDrivers = delivery.assignedUsers
        .filter(user => user._id !== delivery.primaryDriver?._id)
        .map(user => user.name || user.email?.split('@')[0] || 'Driver');
      
      drivers.push(...additionalDrivers);
    }
    
    return drivers.length > 0 ? drivers.join(', ') : 'TBD';
  };

  // ✅ NEW: Calculate combined totals
  const getCombinedTotals = () => {
    const deliveryTotals = {
      orders: deliveryRuns.reduce((total, run) => total + run.totalOrders, 0),
      customers: deliveryRuns.reduce((total, run) => total + run.totalCustomers, 0),
      weight: deliveryRuns.reduce((total, run) => total + getTotalWeight(run.aggregatedProducts), 0)
    };

    const pickupTotals = {
      orders: pickupOrders.totalOrders || 0,
      customers: pickupOrders.totalCustomers || 0,
      weight: pickupOrders.totalWeight || 0
    };

    return {
      delivery: deliveryTotals,
      pickup: pickupTotals,
      combined: {
        orders: deliveryTotals.orders + pickupTotals.orders,
        customers: deliveryTotals.customers + pickupTotals.customers,
        weight: deliveryTotals.weight + pickupTotals.weight
      }
    };
  };

  const totals = getCombinedTotals();

  return (
    <div className={styles.pageContainer}>
      <div className={styles.headerSection}>
        <h1 className={styles.pageTitle}>📦 Warehouse Prep - Deliveries & Pickups</h1>
        
        <div className={styles.dateToggleContainer}>
          <div className={styles.toggleButtons}>
            <button
              className={`${styles.toggleButton} ${
                selectedDate === "today" ? styles.activeToggle : styles.inactiveToggle
              }`}
              onClick={() => handleDateToggle("today")}
            >
              📅 Today
              <span className={styles.toggleDate}>
                {dayjs(currentDate).format("MMM DD")}
              </span>
            </button>
            <button
              className={`${styles.toggleButton} ${
                selectedDate === "tomorrow" ? styles.activeToggle : styles.inactiveToggle
              }`}
              onClick={() => handleDateToggle("tomorrow")}
            >
              🗓️ Tomorrow
              <span className={styles.toggleDate}>
                {dayjs(tomorrowDate).format("MMM DD")}
              </span>
            </button>
          </div>
        </div>

        <div className={styles.dateInfo}>
          <span className={styles.dateLabel}>Showing orders for:</span>
          <span className={styles.dateValue}>
            {dateInfo.label} - {dateInfo.date}
          </span>
          <span className={styles.dayName}>({dateInfo.dayName})</span>
        </div>
      </div>

      {loading && (
        <div className={styles.loadingContainer}>
          <div className={styles.loadingSpinner}></div>
          <p className={styles.loadingText}>
            Loading {selectedDate}'s delivery runs and pickup orders...
          </p>
        </div>
      )}

      {error && (
        <div className={styles.errorContainer}>
          <p className={styles.errorText}>⚠️ {error}</p>
          <button
            onClick={() => {
              fetchDeliveryRuns();
              fetchPickupOrders();
            }}
            className={styles.retryButton}
          >
            🔄 Retry
          </button>
        </div>
      )}

      {!loading && !error && (
        <div className={styles.contentContainer}>
          {deliveryRuns.length === 0 && (!pickupOrders.orders || pickupOrders.orders.length === 0) ? (
            <div className={styles.emptyState}>
              <div className={styles.emptyIcon}>📭</div>
              <h3>No Orders Scheduled</h3>
              <p>
                There are no delivery runs or pickup orders scheduled for {dateInfo.label.toLowerCase()} (
                {dateInfo.date})
              </p>
              <div className={styles.emptySuggestion}>
                <p>
                  Try checking{" "}
                  <button
                    className={styles.suggestionLink}
                    onClick={() =>
                      handleDateToggle(selectedDate === "today" ? "tomorrow" : "today")
                    }
                  >
                    {selectedDate === "today" ? "tomorrow" : "today"} instead
                  </button>
                </p>
              </div>
            </div>
          ) : (
            <>
              {/* ✅ UPDATED: Enhanced summary bar with delivery/pickup breakdown */}
              <div className={styles.summaryBar}>
                <div className={styles.summaryItem}>
                  <span className={styles.summaryNumber}>
                    {totals.combined.orders}
                  </span>
                  <span className={styles.summaryLabel}>
                    Total Orders
                  </span>
                  <div className={styles.summaryBreakdown}>
                    🚚 {totals.delivery.orders} delivery • 📦 {totals.pickup.orders} pickup
                  </div>
                </div>
                <div className={styles.summaryItem}>
                  <span className={styles.summaryNumber}>
                    {deliveryRuns.length}
                  </span>
                  <span className={styles.summaryLabel}>
                    Delivery Runs
                  </span>
                </div>
                <div className={styles.summaryItem}>
                  <span className={styles.summaryNumber}>
                    {totals.combined.customers}
                  </span>
                  <span className={styles.summaryLabel}>
                    Total Customers
                  </span>
                </div>
                <div className={styles.summaryItem}>
                  <span className={styles.summaryNumber}>
                    {totals.combined.weight.toFixed(1)}kg
                  </span>
                  <span className={styles.summaryLabel}>
                    Total Weight
                  </span>
                </div>
              </div>

              {/* ✅ NEW: Pickup Orders Section */}
              {pickupOrders.orders && pickupOrders.orders.length > 0 && (
                <div className={styles.pickupSection}>
                  <div className={styles.sectionHeader}>
                    <h2 className={styles.sectionTitle}>
                      Pickup Orders ({pickupOrders.totalOrders})
                    </h2>
                    <p className={styles.sectionSubtitle}>
                      Orders for customer collection from warehouse
                    </p>
                  </div>

                  <div className={styles.pickupCard}>
                    <div className={styles.pickupCardHeader}>
                      <div className={styles.pickupStats}>
                        <div className={styles.statItem}>
                          <span className={styles.statNumber}>{pickupOrders.totalOrders}</span>
                          <span className={styles.statLabel}>Orders</span>
                        </div>
                        <div className={styles.statItem}>
                          <span className={styles.statNumber}>{pickupOrders.totalCustomers}</span>
                          <span className={styles.statLabel}>Customers</span>
                        </div>
                        <div className={styles.statItem}>
                          <span className={styles.statNumber}>{pickupOrders.totalWeight.toFixed(1)}kg</span>
                          <span className={styles.statLabel}>Total Weight</span>
                        </div>
                        <div className={styles.statItem}>
                          <span className={styles.statNumber}>{pickupOrders.aggregatedProducts.length}</span>
                          <span className={styles.statLabel}>Products</span>
                        </div>
                      </div>
                    </div>

                    {/* ✅ NEW: Pickup Order Comments */}
                    {pickupOrders.hasOrderComments && (
                      <div className={styles.orderCommentsSection}>
                        <h4 className={styles.orderCommentsTitle}>
                          📝 Pickup Order Comments & Special Instructions
                        </h4>
                        <div className={styles.orderCommentsList}>
                          {pickupOrders.orderComments.map((commentData, commentIndex) => (
                            <div key={commentIndex} className={styles.orderCommentItem}>
                              <div className={styles.commentHeader}>
                                <span className={styles.orderBadge}>
                                  #{commentData.orderNumber}
                                </span>
                                <span className={styles.pickupBadge}>
                                  📦 Pickup
                                </span>
                              </div>
                              <div className={styles.commentText}>
                                {commentData.comment}
                              </div>
                              <div className={styles.commentMeta}>
                                📍 {commentData.businessName}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* ✅ NEW: Pickup Products Table */}
                    <div className={styles.productsTable}>
                      <h4 className={styles.tableTitle}>
                        📦 Products to Prepare for Pickup
                      </h4>
                      <div className={styles.tableWrapper}>
                        <table className={styles.productTable}>
                          <thead>
                            <tr>
                              <th>Product Name</th>
                              <th>Quantity</th>
                              <th>Unit</th>
                              <th>Total Weight</th>
                              <th>In Orders</th>
                            </tr>
                          </thead>
                          <tbody>
                            {pickupOrders.aggregatedProducts
                              .sort((a, b) => a.name.localeCompare(b.name))
                              .map((product, productIndex) => (
                                <tr key={productIndex} className={styles.productRow}>
                                  <td className={styles.productNameCell}>
                                    <div className={styles.productNameWrapper}>
                                      <span className={styles.productName}>
                                        {product.name}
                                      </span>
                                      {product.sku && (
                                        <span className={styles.productSku}>
                                          SKU: {product.sku}
                                        </span>
                                      )}
                                    </div>
                                  </td>
                                  <td className={styles.quantityCell}>
                                    <span className={`${styles.quantityBadge} ${styles.pickupQuantity}`}>
                                      {product.totalQuantity}
                                    </span>
                                  </td>
                                  <td className={styles.unitCell}>
                                    {product.unit}
                                  </td>
                                  <td className={styles.weightCell}>
                                    <span className={`${styles.weightBadge} ${styles.pickupWeight}`}>
                                      {product.totalWeight.toFixed(1)}kg
                                    </span>
                                  </td>
                                  <td className={styles.ordersCell}>
                                    <div className={styles.ordersList}>
                                      {product.orders && product.orders.length > 0 ? (
                                        <span className={styles.ordersInfo}>
                                          {product.orderCount} order{product.orderCount !== 1 ? 's' : ''}
                                          <div className={styles.orderNumbers}>
                                            {product.orders.slice(0, 3).map((orderNum, idx) => (
                                              <span key={idx} className={`${styles.orderTag} ${styles.pickupOrderTag}`}>
                                                #{orderNum}
                                              </span>
                                            ))}
                                            {product.orders.length > 3 && (
                                              <span className={styles.moreOrders}>
                                                +{product.orders.length - 3} more
                                              </span>
                                            )}
                                          </div>
                                        </span>
                                      ) : (
                                        <span className={styles.noOrders}>-</span>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              ))}
                          </tbody>
                        </table>

                        <div className={styles.tableSummary}>
                          <div className={styles.summaryRow}>
                            <span className={styles.summaryText}>
                              📊 Pickup Summary: {pickupOrders.aggregatedProducts.length} unique products, 
                              {" "}{pickupOrders.totalWeight.toFixed(1)}kg total weight
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* ✅ NEW: Pickup Customers List */}
                    {pickupOrders.customerNames && pickupOrders.customerNames.length > 0 && (
                      <div className={styles.customersSection}>
                        <h4 className={styles.customersTitle}>
                          👥 Pickup Customers ({pickupOrders.totalCustomers})
                        </h4>
                        <div className={styles.customersList}>
                          {pickupOrders.customerNames.map((customerName, customerIndex) => (
                            <span key={customerIndex} className={`${styles.customerTag} ${styles.pickupCustomerTag}`}>
                              📦 {customerName}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* ✅ UPDATED: Delivery Runs Section */}
              {deliveryRuns.length > 0 && (
                <div className={styles.deliverySection}>
                  <div className={styles.sectionHeader}>
                    <h2 className={styles.sectionTitle}>
                      Delivery Runs ({deliveryRuns.length})
                    </h2>
                    <p className={styles.sectionSubtitle}>
                      Scheduled delivery routes for {dateInfo.label.toLowerCase()}
                    </p>
                  </div>

                  <div className={styles.runsGrid}>
                    {deliveryRuns.map((run, index) => (
                      <div key={run._id} className={styles.runGridItem}>
                        <div className={styles.runCardHeader}>
                          <div className={styles.runNumber}>
                            <h3 className={styles.runTitle}>{run.run}</h3>
                            <span className={styles.runDateBadge}>
                              {dateInfo.label}
                            </span>
                          </div>
                          <div className={styles.runBasicInfo}>
                            <div className={styles.infoItem}>
                              <span className={styles.infoLabel}>Vehicle:</span>
                              <span className={styles.infoValue}>
                                {run.vehicle || "TBD"}
                              </span>
                            </div>
                            
                            <div className={styles.infoItem}>
                              <span className={styles.infoLabel}>Driver:</span>
                              <span className={styles.infoValue}>
                                {formatDriversList(run)}
                              </span>
                            </div>
                            
                            <div className={styles.infoItem}>
                              <span className={styles.infoLabel}>Orders:</span>
                              <span className={styles.infoValue}>
                                {run.totalOrders}
                              </span>
                            </div>
                            <div className={styles.infoItem}>
                              <span className={styles.infoLabel}>Customers:</span>
                              <span className={styles.infoValue}>
                                {run.totalCustomers}
                              </span>
                            </div>
                            <div className={styles.infoItem}>
                              <span className={styles.infoLabel}>
                                Total Weight:
                              </span>
                              <span className={styles.infoValue}>
                                {getTotalWeight(run.aggregatedProducts).toFixed(1)}
                                kg
                              </span>
                            </div>
                          </div>
                        </div>

                        {run.hasOrderComments && (
                          <div className={styles.orderCommentsSection}>
                            <h4 className={styles.orderCommentsTitle}>
                              📝 Order Comments & Special Instructions
                            </h4>
                            <div className={styles.orderCommentsList}>
                              {run.orderComments.map((commentData, commentIndex) => (
                                <div key={commentIndex} className={styles.orderCommentItem}>
                                  <div className={styles.commentHeader}>
                                    <span className={styles.orderBadge}>
                                      #{commentData.orderNumber}
                                    </span>
                                    <span className={styles.displayOrderBadge}>
                                      Delivery #{commentData.displayOrder}
                                    </span>
                                  </div>
                                  <div className={styles.commentText}>
                                    {commentData.comment}
                                  </div>
                                  <div className={styles.commentMeta}>
                                    📍 {commentData.businessName}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        <div className={styles.runCardBody}>
                          <div className={styles.productsTable}>
                            <h4 className={styles.tableTitle}>
                              📦 Products to Prepare for Delivery
                            </h4>
                            {run.aggregatedProducts.length === 0 ? (
                              <div className={styles.noProducts}>
                                No products found for this run
                              </div>
                            ) : (
                              <div className={styles.tableWrapper}>
                                <table className={styles.productTable}>
                                  <thead>
                                    <tr>
                                      <th>Product Name</th>
                                      <th>Quantity</th>
                                      <th>Unit</th>
                                      <th>Total Weight</th>
                                      <th>In Orders</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {run.aggregatedProducts
                                      .sort((a, b) => a.name.localeCompare(b.name))
                                      .map((product, productIndex) => (
                                        <tr
                                          key={productIndex}
                                          className={styles.productRow}
                                        >
                                          <td className={styles.productNameCell}>
                                            <div className={styles.productNameWrapper}>
                                              <span className={styles.productName}>
                                                {product.name}
                                              </span>
                                              {product.sku && (
                                                <span className={styles.productSku}>
                                                  SKU: {product.sku}
                                                </span>
                                              )}
                                            </div>
                                          </td>
                                          <td className={styles.quantityCell}>
                                            <span className={styles.quantityBadge}>
                                              {product.totalQuantity}
                                            </span>
                                          </td>
                                          <td className={styles.unitCell}>
                                            {product.unit}
                                          </td>
                                          <td className={styles.weightCell}>
                                            <span className={styles.weightBadge}>
                                              {product.totalWeight.toFixed(1)}kg
                                            </span>
                                          </td>
                                          <td className={styles.ordersCell}>
                                            <div className={styles.ordersList}>
                                              {product.orders && product.orders.length > 0 ? (
                                                <span className={styles.ordersInfo}>
                                                  {product.orderCount} order{product.orderCount !== 1 ? 's' : ''}
                                                  <div className={styles.orderNumbers}>
                                                    {product.orders.slice(0, 3).map((orderNum, idx) => (
                                                      <span key={idx} className={styles.orderTag}>
                                                        #{orderNum}
                                                      </span>
                                                    ))}
                                                    {product.orders.length > 3 && (
                                                      <span className={styles.moreOrders}>
                                                        +{product.orders.length - 3} more
                                                      </span>
                                                    )}
                                                  </div>
                                                </span>
                                              ) : (
                                                <span className={styles.noOrders}>-</span>
                                              )}
                                            </div>
                                          </td>
                                        </tr>
                                      ))}
                                  </tbody>
                                </table>

                                <div className={styles.tableSummary}>
                                  <div className={styles.summaryRow}>
                                    <span className={styles.summaryText}>
                                      📊 Total: {run.aggregatedProducts.length} unique products, 
                                      {" "}{getTotalWeight(run.aggregatedProducts).toFixed(1)}kg total weight
                                    </span>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>

                          {run.comments && (
                            <div className={styles.deliveryCommentsSection}>
                              <h4 className={styles.deliveryCommentsTitle}>
                                🚚 Delivery Notes
                              </h4>
                              <p className={styles.deliveryComments}>{run.comments}</p>
                            </div>
                          )}

                          {run.customerNames && run.customerNames.length > 0 && (
                            <div className={styles.customersSection}>
                              <h4 className={styles.customersTitle}>
                                👥 Customers ({run.totalCustomers})
                              </h4>
                              <div className={styles.customersList}>
                                {run.customerNames.map((customerName, customerIndex) => (
                                  <span key={customerIndex} className={styles.customerTag}>
                                    {customerName}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default WarehousePrep;